// src/components/pages/donations/LandMapPage.tsx
//
// ✅ Land Map Page (same location-card design/pattern as mosque page)
// - Uses API: GET /api/donations/lands/map-points
// - Reuses LandFilters component
// - Supports ONLY these 2 paths:
//   1) Region + City
//   2) Location-based search (picked on map or current device location)
// - Shows results on Google Map
// - Keeps the SAME location buttons design/pattern as mosque map page
// - Uses Arabic translations from locales file

import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  MarkerF,
  InfoWindowF,
  useJsApiLoader,
} from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "../../../utils/i18nStub";
import { GOOGLE_MAPS_API_KEY } from "../../../config";
import Breadcrumbs, { Crumb } from "../../../components/navigation/Breadcrumbs";
import LandFilters, { FilterState } from "./LandFilters";
import {
  getDonationLandMapPoints,
  LandMapPoint,
} from "../../../services/donationsService";

// -----------------------------
// Types
// -----------------------------
type LatLng = { lat: number; lng: number };

// -----------------------------
// Constants
// -----------------------------
const DEFAULT_CENTER: LatLng = { lat: 24.7136, lng: 46.6753 };
const DEFAULT_ZOOM = 11;

/**
 * Same search-radius style as mosque page.
 * UI shows km, backend receives meters.
 */
const RADIUS_KM_OPTIONS = [1, 2, 3, 5, 10, 20] as const;
type RadiusKm = (typeof RADIUS_KM_OPTIONS)[number];

function kmToMeters(km: number) {
  return Math.round(km * 1000);
}

function buildGoogleMapsPinUrl(dest: LatLng) {
  return `https://www.google.com/maps?q=${encodeURIComponent(`${dest.lat},${dest.lng}`)}`;
}

function buildGoogleMapsDirectionsUrl(origin: LatLng, dest: LatLng) {
  const o = `${origin.lat},${origin.lng}`;
  const d = `${dest.lat},${dest.lng}`;
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
    o
  )}&destination=${encodeURIComponent(d)}&travelmode=driving`;
}

/**
 * Land filters empty state
 */
const emptyLandFilters: FilterState = {
  regionId: null,
  cityId: null,
  centerId: null,
  minPrice: null,
  maxPrice: null,
  searchKeyword: "",

};

export default function LandMapPage() {
  const { t } = useTranslation("MosqueMap");
  const navigate = useNavigate();

  /**
   * Translation helper:
   * if translation key is missing, fallback Arabic text is shown.
   */
  const tt = (key: string, fallback: string) => {
    const v = t(key);
    const last = key.includes(".") ? key.split(".").pop() : key;
    if (!v || v === key || v === last) return fallback;
    return v;
  };

  // -----------------------------
  // Google Maps loader
  // -----------------------------
  const GMAP_LIBS: ("places" | "geometry" | "drawing" | "visualization")[] = [];

    const { isLoaded, loadError } = useJsApiLoader({
    id: "emasajid-google-maps",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY || "",
    libraries: GMAP_LIBS,
    });


  // -----------------------------
  // Filters
  // -----------------------------
  const [filters, setFilters] = useState<FilterState>(emptyLandFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<FilterState>(emptyLandFilters);

  /**
   * Force LandFilters component to remount when we need visual reset
   * (same idea used in mosque page).
   */
  const [filtersResetKey, setFiltersResetKey] = useState(0);

  // -----------------------------
  // Location state
  // -----------------------------
  const [radiusKm, setRadiusKm] = useState<RadiusKm>(2);

  /**
   * userLoc = device current location
   * pickedLoc = location selected by clicking on the map
   * effectiveLoc = whichever one is active
   */
  const [userLoc, setUserLoc] = useState<LatLng | null>(null);
  const [pickedLoc, setPickedLoc] = useState<LatLng | null>(null);
  const [isPicking, setIsPicking] = useState(false);

  const effectiveLoc = pickedLoc ?? userLoc ?? null;

  const [geoError, setGeoError] = useState<string | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [uiError, setUiError] = useState<string>("");

  // -----------------------------
  // Data state
  // -----------------------------
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<LandMapPoint[]>([]);
  const [selected, setSelected] = useState<LandMapPoint | null>(null);

  // -----------------------------
  // Map state
  // -----------------------------
  const [mapCenter, setMapCenter] = useState<LatLng>(DEFAULT_CENTER);
  const [mapZoom, setMapZoom] = useState<number>(DEFAULT_ZOOM);

  const mapRef = useRef<google.maps.Map | null>(null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const panTo = useCallback((pos: LatLng, zoom?: number) => {
    if (!mapRef.current) return;
    mapRef.current.panTo(pos);
    if (typeof zoom === "number") mapRef.current.setZoom(zoom);
  }, []);

  // -----------------------------
  // Map click = pick location
  // -----------------------------
  const onMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      // Only allow map click selection when pick mode is enabled
      if (!isPicking) return;

      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null || lng == null) return;

      const p = { lat, lng };

      // Switch to location mode
      setPickedLoc(p);
      setUserLoc(null);
      setIsPicking(false);

      setUiError("");
      setGeoError(null);

      // When moving to location mode, visually reset region/city filters
      setFilters(emptyLandFilters);
      setAppliedFilters(emptyLandFilters);
      setFiltersResetKey((k) => k + 1);

      setMapCenter(p);
      setMapZoom(13);
      panTo(p, 13);
    },
    [isPicking, panTo]
  );

  // -----------------------------
  // Location actions
  // -----------------------------
  const requestUserLocation = useCallback(() => {
    setGeoError(null);
    setUiError("");
    setGeoLoading(true);

    if (!navigator.geolocation) {
      setGeoLoading(false);
      setGeoError(tt("messages.geoNotSupported", "المتصفح لا يدعم تحديد الموقع."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };

        // Switch to location mode, so clear region/city filters visually + logically
        setFilters(emptyLandFilters);
        setAppliedFilters(emptyLandFilters);
        setFiltersResetKey((k) => k + 1);

        setUserLoc(p);
        setPickedLoc(null);
        setIsPicking(false);

        setGeoLoading(false);

        setMapCenter(p);
        setMapZoom(13);
        panTo(p, 13);
      },
      (err) => {
        setGeoLoading(false);
        setGeoError(
          err.message || tt("messages.locationDenied", "تم رفض الوصول إلى الموقع.")
        );
        setUserLoc(null);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60_000 }
    );
  }, [panTo, t]);

  const togglePickLocation = useCallback(() => {
    setUiError("");
    setGeoError(null);
    setIsPicking((v) => !v);
  }, []);

  /**
   * Clear only location mode
   * - clear user/picked location
   * - keep filters if user already has region/city applied
   * - if no region/city exists, clear results as well
   */
  const clearAllLocation = useCallback(() => {
    setIsPicking(false);
    setPickedLoc(null);
    setUserLoc(null);

    setGeoError(null);
    setUiError("");

    setRadiusKm(2);

    const hasRegionCity =
      !!appliedFilters.regionId && !!appliedFilters.cityId;

    if (!hasRegionCity) {
      setItems([]);
      setSelected(null);
    }
  }, [appliedFilters.regionId, appliedFilters.cityId]);

  // -----------------------------
  // Filters apply/reset
  // -----------------------------
  const handleApplyFilters = useCallback((f: FilterState) => {
    // Switching back to region/city mode => clear location mode first
    clearAllLocation();

    setFilters(f);
    setAppliedFilters(f);
  }, [clearAllLocation]);

  // -----------------------------
  // Build query
  // -----------------------------
  const query = useMemo(() => {
    return {
      regionId: appliedFilters.regionId ?? undefined,
      cityId: appliedFilters.cityId ?? undefined,
      centerId: appliedFilters.centerId ?? undefined,
      minPrice: appliedFilters.minPrice ?? undefined,
      maxPrice: appliedFilters.maxPrice ?? undefined,
      searchKeyword: appliedFilters.searchKeyword?.trim() || undefined,
      userLat: effectiveLoc?.lat,
      userLng: effectiveLoc?.lng,
      radiusM: effectiveLoc ? kmToMeters(radiusKm) : undefined,
      limit: 2000,
    };
  }, [appliedFilters, effectiveLoc, radiusKm]);

  // -----------------------------
  // Fetch data
  // -----------------------------
  const doSearch = useCallback(async () => {
    setUiError("");
    setSelected(null);

    const hasRegionCity =
      !!appliedFilters.regionId && !!appliedFilters.cityId;

    if (!effectiveLoc && !hasRegionCity) {
      setUiError(
        tt(
          "messages.mustSelectRegionCity",
          "يرجى اختيار المنطقة والمدينة أو اختيار موقع من الخريطة ثم الضغط على بحث."
        )
      );
      setItems([]);
      return;
    }

    setLoading(true);

    try {
      const data = await getDonationLandMapPoints(query);

      const valid = data.filter(
        (x) => x.latitude != null && x.longitude != null
      );

      setItems(valid);

      if (effectiveLoc) {
        setMapCenter(effectiveLoc);
        setMapZoom(13);
        panTo(effectiveLoc, 13);
      } else if (valid.length > 0) {
        const p = {
          lat: Number(valid[0].latitude),
          lng: Number(valid[0].longitude),
        };
        setMapCenter(p);
        setMapZoom(12);
        panTo(p, 12);
      }
    } catch (e: any) {
      setItems([]);
      setUiError(
        e?.message || tt("messages.failedToLoad", "فشل تحميل الأراضي.")
      );
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, effectiveLoc, panTo, query, t]);

  React.useEffect(() => {
    doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // -----------------------------
  // Breadcrumbs
  // -----------------------------
  const crumbs: Crumb[] = useMemo(
    () => [
      { label: tt("breadcrumbs.home", "الرئيسية"), href: "/" },
      { label: tt("breadcrumbs.landMap", "خريطة الأراضي المتاحة لبناء مسجد") },
    ],
    [t]
  );

  // -----------------------------
  // Open selected point in Google Maps
  // -----------------------------
  const openInGoogleMaps = useCallback(() => {
    const lat = selected?.latitude;
    const lng = selected?.longitude;
    if (lat == null || lng == null) return;

    const dest = { lat: Number(lat), lng: Number(lng) };
    const url = effectiveLoc
      ? buildGoogleMapsDirectionsUrl(effectiveLoc, dest)
      : buildGoogleMapsPinUrl(dest);

    window.open(url, "_blank", "noopener,noreferrer");
  }, [selected, effectiveLoc]);

  // -----------------------------
  // Layout tokens
  // -----------------------------
  const pageWrap = "max-w-7xl mx-auto px-4 md:px-6 py-16";
  const locationCard =
    "relative w-full bg-white rounded-[32px] border border-[#E5E7EB] shadow-[0_12px_40px_rgba(16,24,40,0.08)] px-6 md:px-8 py-6";

  return (
    <div className={pageWrap}>
      <div className="space-y-6">
        <Breadcrumbs items={crumbs} />

        {/* Filters */}
        <LandFilters
          key={`land-filters-${filtersResetKey}`}
          initial={filters}
          onApply={handleApplyFilters}
        />

        {/* SAME location-card design/pattern as mosque page */}
        <div className={locationCard}>
          <div className="text-[15px] font-semibold text-[#111827] text-center w-full">
            {tt("labels.radius", "نطاق البحث")}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 mt-3">
            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
              {/* Use current device location */}
              <button
                type="button"
                onClick={requestUserLocation}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
                disabled={geoLoading}
              >
                {geoLoading
                  ? tt("buttons.gettingLocation", "جاري تحديد الموقع...")
                  : tt("buttons.useMyLocation", "استخدم موقعي")}
              </button>

              {/* Enable/disable picking location directly from map */}
              <button
                type="button"
                onClick={togglePickLocation}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
              >
                {isPicking
                  ? tt("buttons.cancelPick", "إلغاء الاختيار")
                  : tt("buttons.pickLocation", "اختيار موقع على الخريطة")}
              </button>

              {/* Clear current/picked location */}
              <button
                type="button"
                onClick={clearAllLocation}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center gap-1.5 text-xs whitespace-nowrap h-[42px]"
                disabled={!userLoc && !pickedLoc && !isPicking}
                title={tt("buttons.clearLocation", "مسح الموقع")}
              >
                {tt("buttons.clearLocation", "مسح الموقع")}
              </button>
            </div>

            {/* Radius dropdown */}
            <div className="min-w-[240px] w-full md:w-[280px]">
              <select
                className="modern-filter-select font-arabic"
                value={radiusKm}
                onChange={(e) => setRadiusKm(Number(e.target.value) as RadiusKm)}
                disabled={!effectiveLoc}
              >
                {RADIUS_KM_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {k} km
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Geolocation errors */}
          {geoError && (
            <div className="mt-4 rounded-[18px] border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
              {geoError}
            </div>
          )}

          {/* General UI errors */}
          {uiError && (
            <div className="mt-4 rounded-[18px] border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
              {uiError}
            </div>
          )}

          {/* Picking mode helper */}
          {isPicking && (
            <div className="mt-4 rounded-[18px] border border-[#DBEAFE] bg-[#EFF6FF] px-4 py-3 text-sm text-[#1D4ED8]">
              {tt("messages.clickMapToPick", "اضغط على الخريطة لاختيار موقع.")}
            </div>
          )}

          {/* Status line */}
          <div className="mt-5 text-[15px] text-[#6B7280] font-semibold text-center">
            {effectiveLoc ? (
              <span>
                {tt("status.locationMode", "وضع الموقع")}:{" "}
                <b className="text-[#111827]">
                  {effectiveLoc.lat.toFixed(5)}, {effectiveLoc.lng.toFixed(5)}
                </b>{" "}
                | {tt("labels.radius", "نطاق البحث")}:{" "}
                <b className="text-[#111827]">{radiusKm} km</b>
              </span>
            ) : (
              <span>
                {tt("status.regionCityMode", "وضع المنطقة/المدينة")}:{" "}
                <b className="text-[#111827]">
                  {appliedFilters.regionId && appliedFilters.cityId
                    ? tt("status.ready", "تلقائي")
                    : tt("status.selectRegionCity", "اختر المنطقة + المدينة")}
                </b>
              </span>
            )}
            <span className="mx-2">|</span>
            {tt("status.points", "عدد النتائج")}:{" "}
            <b className="text-[#111827]">{items.length}</b>
          </div>
        </div>

        {/* Map card */}
        <div className="dga-card" style={{ borderRadius: "32px", overflow: "hidden" }}>
          <div style={{ height: "70vh", width: "100%" }}>
            {!GOOGLE_MAPS_API_KEY ? (
              <div className="p-4 text-sm">
                {tt("messages.missingApiKey", "مفتاح Google Maps غير موجود في ملف الإعدادات.")}
              </div>
            ) : loadError ? (
              <div className="p-4 text-sm">
                {tt("messages.mapLoadFailed", "فشل تحميل الخريطة.")}: {String(loadError)}
              </div>
            ) : !isLoaded ? (
              <div className="p-4 text-sm">
                {tt("messages.loadingMap", "جاري تحميل الخريطة...")}
              </div>
            ) : (
              <GoogleMap
                onLoad={onMapLoad}
                onClick={onMapClick}
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={mapCenter}
                zoom={mapZoom}
                options={{
                  clickableIcons: false,
                  mapTypeControl: false,
                  fullscreenControl: true,
                  streetViewControl: false,
                }}
              >
                {/* Device current location marker */}
                {userLoc && (
                  <MarkerF
                    position={userLoc}
                    title={tt("infoWindow.yourLocation", "موقعك")}
                    onClick={() => setSelected(null)}
                  />
                )}

                {/* Map-picked location marker */}
                {pickedLoc && (
                  <MarkerF
                    position={pickedLoc}
                    title={tt("infoWindow.selectedLocation", "الموقع المختار")}
                    onClick={() => setSelected(null)}
                  />
                )}

                {/* Land markers */}
                {items.map((m) => {
                  if (m.latitude == null || m.longitude == null) return null;

                  return (
                    <MarkerF
                      key={`${m.id}-${m.latitude}-${m.longitude}`}
                      position={{ lat: Number(m.latitude), lng: Number(m.longitude) }}
                      onClick={() => setSelected(m)}
                      title={m.fullAddress || tt("infoWindow.landOpportunity", "فرصة أرض")}
                    />
                  );
                })}

                {/* Selected marker info window */}
                {selected && selected.latitude != null && selected.longitude != null && (
                  <InfoWindowF
                    position={{
                      lat: Number(selected.latitude),
                      lng: Number(selected.longitude),
                    }}
                    onCloseClick={() => setSelected(null)}
                  >
                    <div style={{ maxWidth: 320 }}>
                      <div className="font-semibold">
                        {selected.fullAddress || tt("infoWindow.landOpportunity", "فرصة أرض")}
                      </div>

                      <div className="mt-2 text-sm">
                        <div>
                          <b>{tt("infoWindow.landNumber", "رقم الأرض")}:</b>{" "}
                          {selected.landNumber ?? "—"}
                        </div>

                        <div>
                          <b>{tt("infoWindow.sequence", "التسلسل")}:</b>{" "}
                          {selected.sequence ?? "—"}
                        </div>

                        <div>
                          <b>{tt("infoWindow.area", "المساحة")}:</b>{" "}
                          {selected.buildingArea ?? "—"}
                        </div>

                        <div>
                          <b>{tt("infoWindow.price", "السعر التقريبي")}:</b>{" "}
                          {selected.aproxLandPrice ?? "—"}
                        </div>

                        {selected.distanceM != null && (
                          <div>
                            <b>{tt("infoWindow.distance", "المسافة")}:</b>{" "}
                            {(Number(selected.distanceM) / 1000).toFixed(2)} km
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/benefaction/lands/${selected.id}`, {
                            state: { fromMap: true, source: "land-map" },
                          });
                        }}
                        className="mt-3 w-full rounded-xl bg-sa-600 px-6 py-3 text-base font-semibold text-white hover:bg-sa-700 transition-all duration-300"
                      >
                        عرض التفاصيل
                      </button>

                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          className="dga-btn dga-btn-secondary"
                          style={{
                            width: 34,
                            height: 34,
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onClick={openInGoogleMaps}
                          title={tt("buttons.openInGoogleMaps", "فتح في خرائط Google")}
                          aria-label={tt("buttons.openInGoogleMaps", "فتح في خرائط Google")}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                          >
                            <path
                              d="M12 22s7-5.6 7-13a7 7 0 1 0-14 0c0 7.4 7 13 7 13z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinejoin="round"
                            />
                            <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" />
                          </svg>
                        </button>
                      </div>

                      {!effectiveLoc && (
                        <div className="mt-2 text-xs opacity-70">
                          {tt(
                            "infoWindow.tipForDirections",
                            'معلومة: استخدم "استخدم موقعي" أو "اختيار موقع" لفتح الاتجاهات تلقائيًا.'
                          )}
                        </div>
                      )}
                    </div>
                  </InfoWindowF>
                )}
              </GoogleMap>
            )}
          </div>
        </div>id: "emasajid-land-map",id: "emasajid-land-map",

        {/* Loading indicator under map area if needed */}
        {loading && (
          <div className="text-center text-sm text-gray-500">
            {tt("messages.loadingResults", "جاري تحميل النتائج...")}
          </div>
        )}
      </div>
    </div>
  );
}